import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import ExchangeRoomsService from 'src/services/ExchangeRoomsService'

export const exchangeRoomsKeys = {
  list: (exchangeType, search = '') => ['exchangeRooms', exchangeType, search],
  summary: exchangeType => ['exchangeRoomsSummary', exchangeType],
  defaultRoom: exchangeType => ['exchangeRoomsDefault', exchangeType],
  members: (exchangeType, roomId, search = '') => [
    'exchangeRoomMembers',
    exchangeType,
    roomId,
    search
  ],
  candidates: (exchangeType, roomId, search) => [
    'exchangeRoomCandidates',
    exchangeType,
    roomId,
    search
  ]
}

const normalizeListResponse = response => ({
  items: response?.data || [],
  meta: response?.response?.meta || null,
  links: response?.response?.links || null
})

const getNextPageFromLink = nextLink => {
  if (!nextLink) {
    return undefined
  }

  try {
    const url = new URL(nextLink, 'https://econtainers.local')
    const page = Number(url.searchParams.get('page'))

    return Number.isFinite(page) ? page : undefined
  } catch (error) {
    return undefined
  }
}

const getNextPageParam = lastPage => {
  const nextPageFromLink = getNextPageFromLink(lastPage?.links?.next)

  if (nextPageFromLink) {
    return nextPageFromLink
  }

  const currentPage = Number(
    lastPage?.meta?.currentPage || lastPage?.meta?.current_page || 1
  )
  const lastPageNumber = Number(
    lastPage?.meta?.lastPage || lastPage?.meta?.last_page || currentPage
  )

  return currentPage < lastPageNumber ? currentPage + 1 : undefined
}

const getRoomSearchFromQueryKey = queryKey => queryKey?.[2] || ''

const roomMatchesSearch = (room, search = '') => {
  const normalizedSearch = search.trim().toLowerCase()

  if (!normalizedSearch) {
    return true
  }

  return String(room?.name || '')
    .trim()
    .toLowerCase()
    .includes(normalizedSearch)
}

const updateMetaTotal = (meta, delta) => {
  const currentTotal = Number(meta?.total)

  if (!Number.isFinite(currentTotal)) {
    return meta || null
  }

  return {
    ...meta,
    total: Math.max(currentTotal + delta, 0)
  }
}

const createInfiniteListData = item => ({
  pages: [
    {
      items: [item],
      meta: {
        currentPage: 1,
        lastPage: 1,
        total: 1
      },
      links: null
    }
  ],
  pageParams: [1]
})

const insertRoomIntoListData = (data, room) => {
  if (!data?.pages?.length) {
    return createInfiniteListData(room)
  }

  const roomAlreadyExists = data.pages.some(page =>
    (page?.items || []).some(item => item.id === room.id)
  )

  return {
    ...data,
    pages: data.pages.map((page, index) => ({
      ...page,
      items:
        index === 0
          ? [room, ...(page?.items || []).filter(item => item.id !== room.id)]
          : (page?.items || []).map(item =>
              item.id === room.id ? room : item
            ),
      meta: roomAlreadyExists
        ? page?.meta || null
        : updateMetaTotal(page?.meta, 1)
    }))
  }
}

const updateRoomInListData = (data, room) => {
  if (!data?.pages?.length) {
    return data
  }

  return {
    ...data,
    pages: data.pages.map(page => ({
      ...page,
      items: (page?.items || []).map(item =>
        item.id === room.id ? room : item
      )
    }))
  }
}

const syncDefaultRoomInListData = (data, room) => {
  if (!data?.pages?.length) {
    return data
  }

  return {
    ...data,
    pages: data.pages.map(page => ({
      ...page,
      items: (page?.items || []).map(item => {
        if (item.id === room.id) {
          return room
        }

        return item?.isDefault ? { ...item, isDefault: false } : item
      })
    }))
  }
}

const updateRoomInSummaryData = (data, room) => {
  if (!Array.isArray(data?.data)) {
    return data
  }

  return {
    ...data,
    data: data.data.map(item => {
      if (item.id === room.id) {
        return room
      }

      if (room?.isDefault && item?.isDefault) {
        return {
          ...item,
          isDefault: false
        }
      }

      return item
    })
  }
}

const updateDefaultRoomData = (data, room, shouldClearDefault = false) => {
  if (room?.isDefault) {
    return {
      ...(data || {}),
      data: room
    }
  }

  if (shouldClearDefault || data?.data?.id === room.id) {
    return {
      ...(data || {}),
      data: null
    }
  }

  return data
}

const removeRoomFromListData = (data, roomId) => {
  if (!data?.pages?.length) {
    return data
  }

  let removed = false

  const pages = data.pages.map(page => {
    const currentItems = page?.items || []
    const nextItems = currentItems.filter(item => item.id !== roomId)

    if (nextItems.length !== currentItems.length) {
      removed = true
    }

    return {
      ...page,
      items: nextItems
    }
  })

  if (!removed) {
    return data
  }

  return {
    ...data,
    pages: pages.map(page => ({
      ...page,
      meta: updateMetaTotal(page?.meta, -1)
    }))
  }
}

const syncRoomListCaches = (queryClient, exchangeType, updater) => {
  queryClient
    .getQueriesData({
      queryKey: ['exchangeRooms', exchangeType]
    })
    .forEach(([queryKey, data]) => {
      queryClient.setQueryData(queryKey, updater(data, queryKey))
    })
}

const insertRoomIntoListCaches = (queryClient, exchangeType, room) => {
  syncRoomListCaches(queryClient, exchangeType, (data, queryKey) => {
    const search = getRoomSearchFromQueryKey(queryKey)

    if (!roomMatchesSearch(room, search)) {
      return data
    }

    return insertRoomIntoListData(data, room)
  })

  queryClient.setQueryData(exchangeRoomsKeys.list(exchangeType, ''), data =>
    insertRoomIntoListData(data, room)
  )
}

const updateRoomInListCaches = (queryClient, exchangeType, room) => {
  syncRoomListCaches(queryClient, exchangeType, (data, queryKey) => {
    const search = getRoomSearchFromQueryKey(queryKey)

    if (!roomMatchesSearch(room, search)) {
      return removeRoomFromListData(data, room.id)
    }

    return room?.isDefault
      ? syncDefaultRoomInListData(data, room)
      : updateRoomInListData(data, room)
  })
}

const removeRoomFromListCaches = (queryClient, exchangeType, roomId) => {
  syncRoomListCaches(queryClient, exchangeType, data =>
    removeRoomFromListData(data, roomId)
  )
}

const findRoomInListData = (data, roomId) => {
  if (!data?.pages?.length) {
    return null
  }

  for (const page of data.pages) {
    const room = (page?.items || []).find(item => item.id === roomId)

    if (room) {
      return room
    }
  }

  return null
}

const findRoomInListCaches = (queryClient, exchangeType, roomId) => {
  const roomEntry = queryClient
    .getQueriesData({
      queryKey: ['exchangeRooms', exchangeType]
    })
    .map(([, data]) => findRoomInListData(data, roomId))
    .find(Boolean)

  return roomEntry || null
}

const findRoomInSummaryCache = (queryClient, exchangeType, roomId) => {
  const data = queryClient.getQueryData(exchangeRoomsKeys.summary(exchangeType))

  return Array.isArray(data?.data)
    ? data.data.find(room => room.id === roomId) || null
    : null
}

const findRoomInCaches = (queryClient, exchangeType, roomId) =>
  findRoomInListCaches(queryClient, exchangeType, roomId) ||
  findRoomInSummaryCache(queryClient, exchangeType, roomId)

const getRoomFromUpdateResponse = (responseRoom, cachedRoom, variables) => {
  const room = {
    ...(cachedRoom || {}),
    ...(responseRoom || {}),
    ...(variables.payload || {}),
    id: responseRoom?.id || cachedRoom?.id || variables.roomId
  }

  return room.id ? room : null
}

const invalidateRoomCollections = async (queryClient, exchangeType, roomId) => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: exchangeRoomsKeys.list(exchangeType)
    }),
    queryClient.invalidateQueries({
      queryKey: exchangeRoomsKeys.summary(exchangeType)
    }),
    queryClient.invalidateQueries({
      queryKey: exchangeRoomsKeys.defaultRoom(exchangeType)
    }),
    roomId
      ? queryClient.invalidateQueries({
          queryKey: ['exchangeRoomMembers', exchangeType, roomId]
        })
      : Promise.resolve(),
    roomId
      ? queryClient.invalidateQueries({
          queryKey: ['exchangeRoomCandidates', exchangeType, roomId]
        })
      : Promise.resolve()
  ])
}

export const useRoomsList = (exchangeType, search = '', options = {}) =>
  useInfiniteQuery({
    queryKey: exchangeRoomsKeys.list(exchangeType, search),
    queryFn: ({ pageParam = 1 }) =>
      ExchangeRoomsService.getRooms(exchangeType, {
        page: pageParam,
        limit: 20,
        ...(search ? { filter: { name: search } } : {})
      }).then(normalizeListResponse),
    initialPageParam: 1,
    getNextPageParam,
    staleTime: 60 * 1000,
    ...options
  })

export const useRoomMembers = (
  exchangeType,
  roomId,
  search = '',
  options = {}
) =>
  useInfiniteQuery({
    queryKey: exchangeRoomsKeys.members(exchangeType, roomId, search),
    queryFn: ({ pageParam = 1 }) =>
      ExchangeRoomsService.getRoomMembers(exchangeType, roomId, {
        page: pageParam,
        limit: 20,
        ...(search ? { filter: { company_name: search } } : {})
      }).then(normalizeListResponse),
    enabled: Boolean(roomId),
    initialPageParam: 1,
    getNextPageParam,
    staleTime: 30 * 1000,
    ...options
  })

export const useRoomsSummary = (exchangeType, options = {}) =>
  useQuery({
    queryKey: exchangeRoomsKeys.summary(exchangeType),
    queryFn: () => ExchangeRoomsService.getRooms(exchangeType, { limit: 100 }),
    staleTime: 60 * 1000,
    ...options
  })

export const useCandidatesSearch = (
  exchangeType,
  roomId,
  search = '',
  options = {}
) =>
  useInfiniteQuery({
    queryKey: exchangeRoomsKeys.candidates(exchangeType, roomId, search),
    queryFn: ({ pageParam = 1 }) =>
      ExchangeRoomsService.searchCandidates(exchangeType, roomId, {
        page: pageParam,
        limit: 20,
        filter: {
          name: search
        }
      }).then(normalizeListResponse),
    enabled: Boolean(roomId && search),
    initialPageParam: 1,
    getNextPageParam,
    staleTime: 0,
    ...options
  })

export const useCreateRoom = (exchangeType, options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: payload =>
      ExchangeRoomsService.createRoom(exchangeType, payload),
    onSuccess: async (response, variables, context) => {
      const createdRoom = response?.data || null

      if (createdRoom?.id) {
        insertRoomIntoListCaches(queryClient, exchangeType, createdRoom)
      }

      await invalidateRoomCollections(queryClient, exchangeType)
      if (options.onSuccess) {
        await options.onSuccess(response, variables, context)
      }
    },
    onError: options.onError
  })
}

export const useUpdateRoom = (exchangeType, options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ roomId, payload }) =>
      ExchangeRoomsService.updateRoom(exchangeType, roomId, payload),
    onSuccess: async (response, variables, context) => {
      const responseRoom = response?.data || null
      const cachedRoom = findRoomInCaches(
        queryClient,
        exchangeType,
        variables.roomId
      )
      const updatedRoom = getRoomFromUpdateResponse(
        responseRoom,
        cachedRoom,
        variables
      )
      const isDefaultUpdate = Object.prototype.hasOwnProperty.call(
        variables.payload,
        'isDefault'
      )
      const shouldClearDefault =
        isDefaultUpdate &&
        variables.payload.isDefault === false &&
        (Boolean(cachedRoom?.isDefault) || Boolean(responseRoom?.isDefault))

      if (updatedRoom?.id) {
        updateRoomInListCaches(queryClient, exchangeType, updatedRoom)
        queryClient.setQueryData(
          exchangeRoomsKeys.summary(exchangeType),
          data => updateRoomInSummaryData(data, updatedRoom)
        )
        queryClient.setQueryData(
          exchangeRoomsKeys.defaultRoom(exchangeType),
          data => updateDefaultRoomData(data, updatedRoom, shouldClearDefault)
        )
      }

      await invalidateRoomCollections(
        queryClient,
        exchangeType,
        variables.roomId
      )

      if (options.onSuccess) {
        await options.onSuccess(response, variables, context)
      }
    },
    onError: options.onError
  })
}

export const useDeleteRoom = (exchangeType, options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: roomId => ExchangeRoomsService.deleteRoom(exchangeType, roomId),
    onSuccess: async (response, deletedRoomId, context) => {
      removeRoomFromListCaches(queryClient, exchangeType, deletedRoomId)
      await invalidateRoomCollections(queryClient, exchangeType, deletedRoomId)
      if (options.onSuccess) {
        await options.onSuccess(response, deletedRoomId, context)
      }
    },
    onError: options.onError
  })
}

export const useAddRoomMember = (exchangeType, options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ roomId, companyId }) =>
      ExchangeRoomsService.addRoomMember(exchangeType, roomId, companyId),
    onSuccess: async (response, variables, context) => {
      await invalidateRoomCollections(
        queryClient,
        exchangeType,
        variables.roomId
      )
      if (options.onSuccess) {
        await options.onSuccess(response, variables, context)
      }
    },
    onError: options.onError
  })
}

export const useRemoveRoomMember = (exchangeType, options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ roomId, memberId }) =>
      ExchangeRoomsService.removeRoomMember(exchangeType, roomId, memberId),
    onSuccess: async (response, variables, context) => {
      await invalidateRoomCollections(
        queryClient,
        exchangeType,
        variables.roomId
      )
      if (options.onSuccess) {
        await options.onSuccess(response, variables, context)
      }
    },
    onError: options.onError
  })
}
